import { Box, Chip, Paper, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material'
import { ArrowForwardIos } from '@mui/icons-material'
import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { HeatersService } from '../../services/heaters.service'
import { useError } from '../../context/error'
import useDashboardPolling from '../../hooks/useDashboardPolling'
import { SectorHeater } from '../../_types/sector-heaters.type'
import { Formatters } from '../../_helpers/formatter.helper'
import TickerLink from '../ticker-link'
import TickerIcon from '../ticker-icon'

export default function HeatersGlance() {
  const { setError } = useError()
  const heatersService = new HeatersService(setError)

  const [sectors, setSectors] = useState<SectorHeater[]>([])
  const [activeIdx, setActiveIdx] = useState(0)
  const [loading, setLoading] = useState(true)

  const fetchData = async () => {
    const res = await heatersService.getCurrentHeaters()
    const all: SectorHeater[] = res?.heaters ?? res ?? []
    const live = (Array.isArray(all) ? all : []).filter(s => !s.isExpired)
    setSectors(live)
    setLoading(false)
  }

  useDashboardPolling(fetchData)

  const active = sectors[activeIdx]

  return (
    <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <Typography variant='h6'>Sector Rotation</Typography>
        <RouterLink to='/sector-heaters' style={{ textDecoration: 'none' }}>
          <Typography
            variant='caption'
            color='primary'
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            View all <ArrowForwardIos sx={{ fontSize: 10 }} />
          </Typography>
        </RouterLink>
      </Box>

      {loading ? (
        <>
          <Skeleton variant='rectangular' height={32} sx={{ my: 1, borderRadius: 1 }} />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant='text' height={28} />
          ))}
        </>
      ) : sectors.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
          No active sectors.
        </Typography>
      ) : (
        <>
          <Tabs
            value={activeIdx}
            onChange={(_, v) => setActiveIdx(v)}
            variant='scrollable'
            scrollButtons='auto'
            sx={{ minHeight: 36, mt: 1, '.MuiTab-root': { minHeight: 36, py: 0.5 } }}
          >
            {sectors.map((s, i) => (
              <Tab key={s.id ?? i} label={s.title} />
            ))}
          </Tabs>

          {active && (
            <Box mt={1}>
              {active.commentary && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{
                    mb: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {active.commentary}
                </Typography>
              )}
              <Box>
                {active.stocks
                  .slice()
                  .sort((a, b) => {
                    const pa = a.currentPX != null ? (a.currentPX - a.entryPX) / a.entryPX : -Infinity
                    const pb = b.currentPX != null ? (b.currentPX - b.entryPX) / b.entryPX : -Infinity
                    return pb - pa
                  })
                  .map(stock => {
                    const pct = stock.currentPX != null
                      ? ((stock.currentPX - stock.entryPX) / stock.entryPX) * 100
                      : null
                    const isPos = (pct ?? 0) >= 0
                    return (
                      <Box
                        key={stock.symbol}
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                        py={0.75}
                        gap={1}
                        sx={{ borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}
                      >
                        <Box display='flex' alignItems='center' gap={1}>
                          <TickerIcon symbol={stock.symbol} />
                          <TickerLink display='link' ticker={stock.symbol} />
                        </Box>
                        <Box display='flex' alignItems='center' gap={1}>
                          <Typography variant='caption' color='text.secondary'>
                            ${Formatters.formatMoney(stock.currentPX ?? 0)}
                          </Typography>
                          {pct != null && (
                            <Chip
                              label={Formatters.formatPercentage(pct)}
                              size='small'
                              color={isPos ? 'success' : 'error'}
                              variant='outlined'
                            />
                          )}
                        </Box>
                      </Box>
                    )
                  })}
              </Box>
            </Box>
          )}
        </>
      )}
    </Paper>
  )
}
